import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { fugueLabProjects } from '../data/portfolio'
import { ContactForm } from '../components/ContactForm'
import { useSEO } from '@/hooks/useSEO'
import { useLuxeHero } from '@gjl/motion/css'
import { useLuxeTextReveal } from '@gjl/motion/css'
import { trackCalendlyClick } from '@/lib/analytics'

export default function HomePage() {
  useSEO({
    title: 'Great Music Deserves to Be Heard',
    description:
      'The digital platform for music organizations: website, applications, email, giving, ticketing, and music planning. Run it yourself or have us run it for you.',
    url: '/',
    canonical: '/',
  })

  // Three featured projects, shown large. Six small cards diluted the work.
  const featuredProjects = useMemo(() => fugueLabProjects.slice(0, 3), [])

  // One orchestrated entrance, on the hero only. The per-section staggered
  // reveals and hover lifts on every card were the generic default; motion
  // below the fold is now limited to responses to the visitor's own hover.
  const heroContentRef = useLuxeHero<HTMLDivElement>(0.4)
  const heroHeadlineRef = useLuxeTextReveal<HTMLHeadingElement>({ type: 'words', delay: 0.6 })

  return (
    <div className="">
      {/* Hero Section */}
      {/* The hero is a short box (76svh) with the content block nudged off true
          centre by the pt/pb asymmetry — `pt` > `pb` drops the headline BELOW
          centre, `pb` > `pt` lifts it above. It ran above centre for a while on
          the optical-centring argument (the eye weights the top edge, so a
          dead-centred block reads low); the current values put it back down
          deliberately. `svh` rather than `vh` so mobile browser chrome does not
          add phantom height. */}
      <section className="relative flex min-h-[76svh] items-center justify-center overflow-hidden pt-20 pb-8">
        {/* `ken-burns` is an 18s one-shot scale 1 -> 1.05 (T0, zero JS). The
            clip loops on a much shorter cycle, so the slow push runs across
            several loops and stops the hero reading as a flat plate. The
            section's `overflow-hidden` is what keeps the scaled edges out. */}
        <video autoPlay muted loop playsInline className="ken-burns absolute inset-0 h-full w-full object-cover">
          <source src="/home-hero-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Three layers where there was one flat 80% plate. The scrim still
            does the contrast work (white-on-video needs it); vignette then
            pulls the corners down so the eye lands on the headline, and grain
            breaks up the banding that a large flat black gradient shows on
            8-bit panels. Both are T0 classes, `pointer-events: none`, and both
            position against this `relative` section. Grain carries `z-index: 1`
            by design, so it sits over the copy too — that is the film look, at
            5% opacity. Tune via --fx-vignette-strength / --fx-grain-opacity;
            do not redeclare the rules. */}
        <div className="absolute inset-0 bg-black/80" />
        <div className="fx-vignette" />
        <div className="fx-grain" />
        <div
          ref={heroContentRef}
          // `max-w-sm` used to apply from 0 all the way to the md breakpoint
          // while the headline scaled with the viewport, so at 744px (iPad mini
          // portrait) the type was 53px inside 352px of measure and wrapped to
          // four lines. One measure that simply tracks the viewport removes the
          // cliff; the paragraphs below set their own narrower measure.
          className="relative w-full max-w-5xl space-y-6 px-4 text-center text-white"
        >
          {/* Two lines, not four. At the top of the clamp (88-96px) this
              headline's natural halves measure ~891px and ~871px, but max-w-4xl
              left only 864px usable — BOTH halves overflowed, so the browser
              cascaded it into four short lines. max-w-5xl gives 992px, which
              clears the wider half at every step of the clamp. `text-balance`
              then evens the two lines instead of leaving a greedy long-short
              break. */}
          {/* `leading-[1.05]` on this h1 only: the shared .text-fluid-h1 is
              leading-tight, and at the top of the clamp the gap between the two
              lines read as two separate statements. */}
          <h1 ref={heroHeadlineRef} className="text-fluid-h1 leading-[1.05] text-balance">
            Great music deserves to be heard.
          </h1>
          {/* One subhead, not two. There used to be a website line ("works as
              hard as you do") above this platform line; the two pitched
              different things and made the hero a wall of text. 720px keeps the
              measure readable (~70ch) against the wider headline. */}
          <p className="text-fluid-body mx-auto max-w-[720px] leading-relaxed text-balance text-white/90">
            One platform for your website, applications, email, giving, and ticketing. Run it yourself, or have us run
            it for you.
          </p>
          <div className="flex flex-col items-center gap-4">
            <a href="/book" target="_blank" rel="noopener noreferrer" onClick={() => trackCalendlyClick('/')}>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </m.div>
            </a>
            <p className="mt-2 max-w-md text-sm text-white/70">15 minutes, no obligation.</p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-28 px-5 py-20 md:space-y-36 md:px-12 md:py-28">
        {/* What we do. The one loud thing below the hero: the three services
            set as a large typographic list, not three icon cards. The old
            card grid (tinted box + lucide icon + hover lift) was the stock
            SaaS kit and read as generated; here the service names themselves
            carry the section. Rows separate by space alone: a drawn rule between
            them is the same AI tell as the cards (see "Visual tells" in
            docs/conventions/bespoke-cms-site-engine.md). Hover changes the name's colour only. */}
        <section aria-labelledby="what-we-do" className="space-y-12">
          <div className="grid gap-6 md:grid-cols-12">
            <h2 id="what-we-do" className="text-fluid-h3 md:col-span-5">
              What we do
            </h2>
            <p className="text-muted-foreground text-fluid-body max-w-[60ch] md:col-span-7">
              We build and run the digital side of classical music organizations: festivals, chamber ensembles, choirs,
              opera companies and sacred music programs. We are musicians ourselves, so we already speak the language.
            </p>
          </div>

          <ul className="space-y-6 md:space-y-4">
            {SERVICES.map((item) => (
              <li key={item.link}>
                <Link
                  to={item.link}
                  className="group focus-visible:outline-primary grid gap-3 py-8 focus-visible:outline-2 focus-visible:outline-offset-4 md:grid-cols-12 md:items-baseline md:gap-8 md:py-10"
                >
                  <h3 className="group-hover:text-primary text-[clamp(2rem,4.5vw,3.75rem)] leading-[1.05] font-normal transition-colors duration-200 md:col-span-7">
                    {item.title}
                  </h3>
                  <span className="text-muted-foreground max-w-[48ch] leading-relaxed md:col-span-5">
                    {item.description}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <Link to="/pricing" className="inline-block">
            <Button variant="default" size="lg" className="cursor-pointer px-8">
              See pricing
            </Button>
          </Link>
        </section>

        {/* How it works. This content IS a sequence, so it keeps its numbers,
            set as large figures in the display face rather than filled circles
            inside cards. An <ol> because the order is the meaning. */}
        <section aria-labelledby="how-it-works" className="space-y-12">
          <h2 id="how-it-works" className="text-fluid-h3">
            How it works
          </h2>
          <ol className="grid gap-12 md:grid-cols-3 md:gap-10">
            {STEPS.map((step, idx) => (
              <li key={step.title} className="space-y-3">
                <span aria-hidden="true" className="font-display text-muted-foreground/60 block text-6xl leading-none">
                  {idx + 1}
                </span>
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="text-muted-foreground max-w-[40ch] leading-relaxed">{step.description}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Recent work: three projects, large and frameless, instead of six
            small bordered cards. The first runs full width; the screenshot is
            the proof, so it gets the room. Each description is the project's
            own portfolio copy, which carries its real result (e.g. Great Host's
            1,312 pages), so no invented "10+ / 5+" stat panel is needed. */}
        <section aria-labelledby="recent-work" className="space-y-12">
          <h2 id="recent-work" className="text-fluid-h3">
            Recent work
          </h2>
          <div className="grid gap-x-10 gap-y-16 md:grid-cols-2">
            {featuredProjects.map((project, idx) => (
              <Link
                key={project.slug}
                to={`/portfolio/${project.slug}`}
                className={`group focus-visible:outline-primary block space-y-5 focus-visible:outline-2 focus-visible:outline-offset-4 ${idx === 0 ? 'md:col-span-2' : ''}`}
              >
                <div
                  className={`bg-surface overflow-hidden rounded-md ${idx === 0 ? 'aspect-[16/9]' : 'aspect-[4/3]'}`}
                >
                  <img
                    src={project.image}
                    alt=""
                    width={idx === 0 ? 1200 : 600}
                    height={idx === 0 ? 675 : 450}
                    loading="lazy"
                    style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02] motion-reduce:transition-none"
                  />
                </div>
                <div className={idx === 0 ? 'grid gap-3 md:grid-cols-12 md:gap-8' : 'space-y-3'}>
                  <h3 className="text-fluid-h4 group-hover:text-primary transition-colors duration-200 md:col-span-5">
                    {project.title}
                  </h3>
                  <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">
                    {project.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/portfolio"
            className="text-primary inline-block font-medium underline decoration-1 underline-offset-4 hover:decoration-2"
          >
            See all projects
          </Link>
        </section>

        {/* Testimonials: one quote given real weight, set in the display face,
            with the other two smaller beneath it. No boxes and no decorative
            quote glyphs; the <blockquote> and the type size do the work. */}
        <section aria-labelledby="clients-say" className="space-y-14">
          <h2 id="clients-say" className="sr-only">
            What clients say
          </h2>
          <figure className="max-w-4xl space-y-6">
            <blockquote className="font-display text-[clamp(1.75rem,3.2vw,2.75rem)] leading-[1.2] tracking-tight text-balance">
              “We diligently searched and interviewed prospective website designers, and they all lacked the expertise
              and understanding you hold in liturgy.”
            </blockquote>
            <figcaption className="text-muted-foreground">
              <span className="text-foreground font-semibold">Susan Jane Matthews, DMA</span>, Founding Coordinator, A
              Great Host of Composers, and Director of Music, St. Paul&apos;s Episcopal Church, Burlingame
            </figcaption>
          </figure>
          <div className="grid gap-12 md:grid-cols-2 md:gap-10">
            {QUOTES.map((q) => (
              <figure key={q.author} className="space-y-4">
                <blockquote className="text-fluid-body leading-relaxed">“{q.quote}”</blockquote>
                <figcaption className="text-muted-foreground text-sm">
                  <span className="text-foreground font-semibold">{q.author}</span>, {q.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <ContactForm
          header="Ready to focus on the music?"
          body="Let's talk about modernizing your online presence, so you can fill more seats, grow your audience, and spend less time on technology."
          showBooking
          bookingHeading="Book a Free Call"
        />
      </div>
    </div>
  )
}

const SERVICES = [
  {
    title: 'Websites',
    link: '/services/websites',
    description:
      'Fast, mobile-friendly sites with an editor your team can use to update concerts and programs without code.',
  },
  {
    title: 'Ticketing and applications',
    link: '/services/digital-systems',
    description: 'Registration, tickets and donations, with payment processing and reporting built in.',
  },
  {
    title: 'Ongoing support',
    link: '/services/digital-partnership',
    description: 'Website updates, email campaigns and strategy from people who understand the music.',
  },
]

const STEPS = [
  {
    title: 'A free strategy call',
    description: "Fifteen minutes. We'll look at your current setup and find the biggest opportunities.",
  },
  {
    title: 'A plan for your organization',
    description: 'A new website, simpler systems, or an ongoing partnership, whichever fits what you need.',
  },
  {
    title: 'Launch, then grow',
    description: 'We build while you focus on the music, then help you reach a bigger audience.',
  },
]

const QUOTES = [
  {
    quote:
      'My experience working with Garrett was a pleasure from start to finish. He was organized, efficient and highly professional in helping to realize my vision for my website... Can’t recommend him highly enough.',
    author: 'Matt Mueller',
    role: 'Baritone',
  },
  {
    quote:
      'I highly recommend enlisting Garrett’s expertise to design (or redesign) your website... he creates a product even better than you could have imagined on your own.',
    author: 'Dr. Susan Bengtson',
    role: 'Violist and educator',
  },
]
